import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Tooltip,
  Alert,
  Spin,
  Empty,
  App,
  Row,
  Col,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  SearchOutlined,
  SyncOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useGetProductsQuery,
} from '../../services/productsApi';
import {
  useGetProductosFacturacionQuery,
  useCreateProductoFacturacionMutation,
  useUpdateProductoFacturacionMutation,
  useDeleteProductoFacturacionMutation,
  useSyncProductoFacturadorMutation,
  useSearchProductosServiciosQuery,
  useGetUnidadesMedidaQuery,
} from '../../services/facturacionApi';
import type { Product } from '../../types/products';
import type { ProductoFacturacion } from '../../types/facturacion';
import { useResponsive } from '../../hooks/useResponsive';

const { Title, Text } = Typography;
const { Option } = Select;

interface ProductoConMapeo extends Product {
  productoFacturacion?: ProductoFacturacion;
  mapeado: boolean;
}

const ProductoFacturacionMapper: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const { isMobile } = useResponsive();
  const [form] = Form.useForm();
  const [showMapeoModal, setShowMapeoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductoConMapeo | null>(null);
  const [productToDelete, setProductToDelete] = useState<{ id: string; name: string } | null>(null);
  const [searchSATTerm, setSearchSATTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'mapped' | 'unmapped'>('all');

  // API Queries
  const { data: products = [], isLoading: loadingProducts } = useGetProductsQuery({ availableOnly: false });
  const { data: productosFacturacion = [], isLoading: loadingMapeos } = useGetProductosFacturacionQuery();
  const { data: unidadesMedida = [] } = useGetUnidadesMedidaQuery();
  const { data: productosSAT = [], isFetching: searchingSAT } = useSearchProductosServiciosQuery(
    debouncedSearchTerm,
    { skip: debouncedSearchTerm.length < 3 }
  );

  // Mutations
  const [createMapeo, { isLoading: creating }] = useCreateProductoFacturacionMutation();
  const [updateMapeo, { isLoading: updating }] = useUpdateProductoFacturacionMutation();
  const [deleteMapeo, { isLoading: deleting }] = useDeleteProductoFacturacionMutation();
  const [syncMapeo, { isLoading: syncing }] = useSyncProductoFacturadorMutation();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchSATTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchSATTerm]);

  // Combine products with their facturacion mapping
  const productosConMapeo: ProductoConMapeo[] = products.map((product) => {
    const mapeo = productosFacturacion.find((pf) => pf.productId === product.id);
    return {
      ...product,
      productoFacturacion: mapeo,
      mapeado: !!mapeo,
    };
  });

  const handleOpenMapeoModal = (product: ProductoConMapeo) => {
    setSelectedProduct(product);
    if (product.productoFacturacion) {
      form.setFieldsValue({
        claveProdServ: product.productoFacturacion.claveProdServ,
        claveUnidad: product.productoFacturacion.claveUnidad,
        descripcion: product.productoFacturacion.descripcion || product.name,
      });
    } else {
      form.setFieldsValue({
        claveProdServ: undefined,
        claveUnidad: undefined,
        descripcion: product.name,
      });
    }
    setShowMapeoModal(true);
  };

  const handleSaveMapeo = async () => {
    if (!selectedProduct) return;

    try {
      const values = await form.validateFields();

      if (selectedProduct.productoFacturacion) {
        // Update existing mapping
        await updateMapeo({
          id: selectedProduct.productoFacturacion.id,
          ...values,
        }).unwrap();
        messageApi.success('Mapeo actualizado correctamente', 3);
      } else {
        // Create new mapping
        await createMapeo({
          productId: selectedProduct.id,
          ...values,
        }).unwrap();
        messageApi.success('Producto mapeado correctamente', 3);
      }

      setShowMapeoModal(false);
      form.resetFields();
      setSelectedProduct(null);
      setSearchSATTerm('');
    } catch (error: any) {
      console.error('Error saving mapeo:', error);
      messageApi.error(error?.data?.message || 'Error al guardar el mapeo', 5);
    }
  };

  const handleDeleteMapeo = (productoFacturacionId: string, productName: string) => {
    setProductToDelete({ id: productoFacturacionId, name: productName });
    setShowDeleteModal(true);
  };

  const confirmDeleteMapeo = async () => {
    if (!productToDelete) return;

    try {
      await deleteMapeo(productToDelete.id).unwrap();
      messageApi.success('Mapeo eliminado correctamente', 3);
      setShowDeleteModal(false);
      setProductToDelete(null);
    } catch (error: any) {
      console.error('Error deleting mapeo:', error);
      messageApi.error(error?.data?.message || 'Error al eliminar el mapeo', 5);
    }
  };

  const handleSyncMapeo = async (productId: string) => {
    try {
      await syncMapeo(productId).unwrap();
      messageApi.success('Producto sincronizado con Facturador API', 3);
    } catch (error: any) {
      console.error('Error syncing mapeo:', error);
      messageApi.error(error?.data?.message || 'Error al sincronizar producto', 5);
    }
  };

  const columns: ColumnsType<ProductoConMapeo> = [
    {
      title: 'Estado',
      key: 'mapeado',
      width: 100,
      align: 'center',
      render: (_, record) => {
        if (!record.mapeado) {
          return (
            <Tag icon={<CloseCircleOutlined />} color="warning">
              Sin Mapear
            </Tag>
          );
        }

        // Tener claves SAT guardadas no basta: mientras el producto no exista en el
        // Facturador (facturadorProductoId), el timbrado lo manda al concepto genérico
        // "CONSUMO DE ALIMENTOS". Antes esto se pintaba "Mapeado" en verde, así que el
        // restaurante creía tener el catálogo listo y sus facturas salían sin desglose.
        if (!record.productoFacturacion?.facturadorProductoId) {
          return (
            <Tooltip title="Las claves SAT están guardadas, pero el producto no se ha sincronizado con el Facturador. Hasta que se sincronice, sus facturas lo agrupan como CONSUMO DE ALIMENTOS.">
              <Tag icon={<SyncOutlined />} color="orange">
                Sin sincronizar
              </Tag>
            </Tooltip>
          );
        }

        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Mapeado
          </Tag>
        );
      },
      filters: [
        { text: 'Mapeados', value: 'synced' },
        { text: 'Sin sincronizar', value: 'pending' },
        { text: 'Sin Mapear', value: 'unmapped' },
      ],
      onFilter: (value, record) => {
        if (!record.mapeado) return value === 'unmapped';
        return record.productoFacturacion?.facturadorProductoId
          ? value === 'synced'
          : value === 'pending';
      },
    },
    {
      title: 'Producto',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Categoría: {record.categoryName || 'Sin categoría'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Precio',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (price) => `$${price?.toFixed(2)}`,
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
    },
    {
      title: 'Clave SAT',
      key: 'claveProdServ',
      width: 150,
      render: (_, record) =>
        record.productoFacturacion ? (
          <Tooltip title={record.productoFacturacion.descripcion}>
            <Tag color="blue">{record.productoFacturacion.claveProdServ}</Tag>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Unidad',
      key: 'claveUnidad',
      width: 120,
      render: (_, record) =>
        record.productoFacturacion ? (
          <Tag>{record.productoFacturacion.claveUnidad}</Tag>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title={record.mapeado ? 'Editar mapeo' : 'Mapear producto'}>
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenMapeoModal(record)}
            >
              {record.mapeado ? 'Editar' : 'Mapear'}
            </Button>
          </Tooltip>
          {record.mapeado && record.productoFacturacion && (
            <>
              {record.productoFacturacion.facturadorProductoId && (
                <Tooltip title="Sincronizar con Facturador API">
                  <Button
                    size="small"
                    icon={<SyncOutlined />}
                    onClick={() => handleSyncMapeo(record.id)}
                    loading={syncing}
                  />
                </Tooltip>
              )}
              <Tooltip title="Eliminar mapeo">
                <Button
                  danger
                  size="small"
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleDeleteMapeo(record.productoFacturacion!.id, record.name)}
                  loading={deleting}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const loading = loadingProducts || loadingMapeos;

  // Filter products based on status
  const filteredProductos = productosConMapeo.filter((p) => {
    if (filterStatus === 'mapped') return p.mapeado;
    if (filterStatus === 'unmapped') return !p.mapeado;
    return true;
  });

  const mapeados = productosConMapeo.filter((p) => p.mapeado).length;
  const sinMapear = productosConMapeo.length - mapeados;

  // Mobile card renderer
  const renderMobileCards = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {filteredProductos.length === 0 ? (
        <Empty
          description="No hay productos que coincidan con el filtro"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        filteredProductos.map((product) => (
          <Card
            key={product.id}
            size="small"
            style={{ borderRadius: 8 }}
            bodyStyle={{ padding: 12 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: 4 }}>
                  {product.mapeado ? (
                    <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 11 }}>
                      Mapeado
                    </Tag>
                  ) : (
                    <Tag icon={<CloseCircleOutlined />} color="warning" style={{ fontSize: 11 }}>
                      Sin Mapear
                    </Tag>
                  )}
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {product.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {product.categoryName || 'Sin categoría'} • ${product.price?.toFixed(2)}
                </div>
              </div>
              <Space size={4}>
                <Button
                  type="primary"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenMapeoModal(product)}
                >
                  {product.mapeado ? 'Editar' : 'Mapear'}
                </Button>
                {product.mapeado && product.productoFacturacion && (
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteMapeo(product.productoFacturacion!.id, product.name)}
                  />
                )}
              </Space>
            </div>
            {product.productoFacturacion && (
              <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 8, marginTop: 4 }}>
                <Row gutter={8}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Clave SAT:</Text>
                    <div>
                      <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                        {product.productoFacturacion.claveProdServ}
                      </Tag>
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Unidad:</Text>
                    <div>
                      <Tag style={{ fontSize: 11, margin: 0 }}>
                        {product.productoFacturacion.claveUnidad}
                      </Tag>
                    </div>
                  </Col>
                </Row>
                {product.productoFacturacion.facturadorProductoId && (
                  <Button
                    size="small"
                    icon={<SyncOutlined />}
                    onClick={() => handleSyncMapeo(product.id)}
                    loading={syncing}
                    style={{ marginTop: 8, width: '100%' }}
                  >
                    Sincronizar con Facturador
                  </Button>
                )}
              </div>
            )}
          </Card>
        ))
      )}
    </div>
  );

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: isMobile ? '24px 0' : '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Cargando productos...</Text>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: isMobile ? 12 : 24 }}>
        <Title level={isMobile ? 4 : 3}>
          <SearchOutlined /> {isMobile ? 'Mapeo SAT' : 'Mapeo de Productos a Claves SAT'}
        </Title>
        {!isMobile && (
          <Text type="secondary">
            Configure las claves de producto/servicio SAT y unidades de medida para cada producto de su menú.
            Esto es necesario para generar facturas electrónicas.
          </Text>
        )}
      </div>

      <Alert
        message={isMobile ? 'Resumen' : 'Resumen de Mapeo'}
        description={
          <div style={{ display: 'flex', gap: isMobile ? 16 : 8, flexDirection: isMobile ? 'row' : 'column' }}>
            <Text style={{ fontSize: isMobile ? 12 : 14 }}>
              <CheckCircleOutlined style={{ color: '#52c41a' }} /> {mapeados} mapeados
            </Text>
            <Text style={{ fontSize: isMobile ? 12 : 14 }}>
              <CloseCircleOutlined style={{ color: '#faad14' }} /> {sinMapear} sin mapear
            </Text>
          </div>
        }
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: isMobile ? 12 : 16 }}
      />

      {/* Filter section */}
      <Card size="small" style={{ marginBottom: isMobile ? 12 : 16 }}>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} sm={8}>
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: '100%' }}
              size={isMobile ? 'middle' : 'middle'}
            >
              <Option value="all">Todos los productos ({productosConMapeo.length})</Option>
              <Option value="mapped">Mapeados ({mapeados})</Option>
              <Option value="unmapped">Sin mapear ({sinMapear})</Option>
            </Select>
          </Col>
          {!isMobile && (
            <Col sm={16}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Mostrando {filteredProductos.length} de {productosConMapeo.length} productos
              </Text>
            </Col>
          )}
        </Row>
      </Card>

      <Card bodyStyle={{ padding: isMobile ? 12 : 24 }}>
        {productosConMapeo.length === 0 ? (
          <Empty
            description="No hay productos en el menú"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : isMobile ? (
          renderMobileCards()
        ) : (
          <Table
            columns={columns}
            dataSource={filteredProductos}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} productos`,
            }}
          />
        )}
      </Card>

      {/* Mapeo Modal */}
      <Modal
        title={
          isMobile
            ? (selectedProduct?.mapeado ? 'Editar Mapeo' : 'Mapear Producto')
            : (selectedProduct?.mapeado
              ? `Editar Mapeo: ${selectedProduct?.name}`
              : `Mapear Producto: ${selectedProduct?.name}`)
        }
        open={showMapeoModal}
        onOk={handleSaveMapeo}
        onCancel={() => {
          setShowMapeoModal(false);
          form.resetFields();
          setSelectedProduct(null);
          setSearchSATTerm('');
        }}
        confirmLoading={creating || updating}
        okText="Guardar"
        cancelText="Cancelar"
        width={isMobile ? '100%' : 700}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto', padding: '12px' } : {}}
      >
        <Form form={form} layout="vertical" size={isMobile ? 'middle' : 'large'}>
          <Alert
            message={isMobile ? selectedProduct?.name : 'Información del Producto'}
            description={
              isMobile ? (
                <Text style={{ fontSize: 12 }}>
                  {selectedProduct?.categoryName || 'Sin categoría'} • ${selectedProduct?.price?.toFixed(2)}
                </Text>
              ) : (
                <div>
                  <Text>
                    <strong>Nombre:</strong> {selectedProduct?.name}
                  </Text>
                  <br />
                  <Text>
                    <strong>Precio:</strong> ${selectedProduct?.price?.toFixed(2)}
                  </Text>
                  <br />
                  <Text>
                    <strong>Categoría:</strong> {selectedProduct?.categoryName || 'Sin categoría'}
                  </Text>
                </div>
              )
            }
            type="info"
            showIcon
            style={{ marginBottom: isMobile ? 12 : 16 }}
          />

          <Form.Item
            name="claveProdServ"
            label={isMobile ? 'Clave SAT' : 'Clave de Producto o Servicio SAT'}
            rules={[
              { required: true, message: 'Seleccione una clave SAT' },
              { len: 8, message: 'La clave debe tener 8 caracteres' },
            ]}
          >
            <Select
              showSearch
              placeholder={isMobile ? "Buscar clave SAT..." : "Buscar clave SAT (ej: 'alimentos', 'bebidas')"}
              onSearch={setSearchSATTerm}
              filterOption={false}
              notFoundContent={
                searchingSAT ? (
                  <div style={{ textAlign: 'center', padding: isMobile ? 12 : 16 }}>
                    <Spin size="small" />
                  </div>
                ) : searchSATTerm.length < 3 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={isMobile ? "Escriba 3+ caracteres" : "Escriba al menos 3 caracteres para buscar"}
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No se encontraron resultados"
                  />
                )
              }
              loading={searchingSAT}
              optionLabelProp="label"
            >
              {productosSAT.map((producto) => (
                <Option
                  key={producto.clave}
                  value={producto.clave}
                  label={`${producto.clave} - ${producto.descripcion}`}
                >
                  <div>
                    <Text strong style={{ fontSize: isMobile ? 12 : 14 }}>{producto.clave}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
                      {producto.descripcion}
                    </Text>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="claveUnidad"
            label="Unidad de Medida"
            rules={[{ required: true, message: 'Seleccione una unidad de medida' }]}
          >
            <Select
              showSearch
              placeholder="Seleccione unidad de medida"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as any)
                  ?.toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {unidadesMedida.map((unidad) => (
                <Option key={unidad.clave} value={unidad.clave}>
                  {unidad.clave} - {unidad.nombre}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="descripcion"
            label={isMobile ? 'Descripción' : 'Descripción para Factura'}
            rules={[
              { required: true, message: 'Ingrese una descripción' },
              { max: 1000, message: 'La descripción no puede exceder 1000 caracteres' },
            ]}
          >
            <Input.TextArea
              rows={isMobile ? 2 : 3}
              placeholder="Descripción que aparecerá en la factura"
              showCount
              maxLength={1000}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="¿Eliminar mapeo?"
        open={showDeleteModal}
        onOk={confirmDeleteMapeo}
        onCancel={() => {
          setShowDeleteModal(false);
          setProductToDelete(null);
        }}
        confirmLoading={deleting}
        okText="Eliminar"
        okType="danger"
        cancelText="Cancelar"
        width={isMobile ? '90%' : 500}
      >
        <Alert
          message={isMobile ? 'Confirmar' : 'Confirmación de Eliminación'}
          description={
            <Text style={{ fontSize: isMobile ? 13 : 14 }}>
              Se eliminará el mapeo del producto "<strong>{productToDelete?.name}</strong>". Esta acción no se puede deshacer.
            </Text>
          }
          type="warning"
          showIcon
          icon={<CloseCircleOutlined />}
        />
      </Modal>
    </div>
  );
};

export default ProductoFacturacionMapper;
